/**
 * Story SF-CHAT-6.5 — decisões da tela live, puras para serem testadas em Node.
 *
 * Extraídas do `LiveChat.tsx` na revisão: a tela só aplica o que estas funções
 * decidem, e cada regra tem teste de comportamento.
 */
import type { AppSessionErrorKind, AppUser } from "./appSession";

export interface VerdictEffect {
  /** Encerrar a sessão do app e mostrar o login. */
  signOut: boolean;
  /** Devolver ao campo o texto que não foi atendido. */
  restoreInput: boolean;
  /** Aviso para a pessoa; `null` quando o texto do turno já basta. */
  notice: string | null;
}

const NONE: VerdictEffect = { signOut: false, restoreInput: false, notice: null };

/**
 * O que fazer com o veredito público do turno.
 *
 * `context_rejected` **não** encerra a sessão: se a causa for permissão, entrar
 * de novo não resolve e a pessoa ficaria num laço de login com 2FA.
 */
export function verdictEffect(status: unknown): VerdictEffect {
  switch (status) {
    case "grant_required":
      return {
        signOut: true,
        restoreInput: true,
        notice: "O chat não recebeu sua autorização para esta mensagem. Entre de novo e reenvie.",
      };
    case "grant_rejected":
      return {
        signOut: true,
        restoreInput: true,
        notice: "Sua autorização para esta mensagem expirou ou já foi usada. Entre de novo e reenvie.",
      };
    case "context_rejected":
      return {
        signOut: false,
        restoreInput: true,
        notice:
          "O aplicativo não confirmou seu acesso para esta consulta. Se continuar, confira no aplicativo se você ainda tem acesso a esta empresa.",
      };
    case "context_expired":
    case "context_unavailable":
      // O texto do turno já pede para reenviar; devolver o texto poupa digitar.
      return { ...NONE, restoreInput: true };
    default:
      return NONE;
  }
}

/** A conversa continua só para a mesma pessoa na mesma empresa. */
export function conversationOwner(user: AppUser): string {
  return `${user.id}:${user.empresaId ?? "-"}`;
}

export function keepsConversation(currentOwner: string | null, user: AppUser): boolean {
  return currentOwner !== null && currentOwner === conversationOwner(user);
}

/**
 * Erro no passo do código: quando o desafio morreu no app (vencido ou
 * tentativas esgotadas), insistir no código só gera outro erro.
 */
export function dropsChallenge(kind: AppSessionErrorKind | null, inChallenge: boolean): boolean {
  return inChallenge && (kind === "reauth" || kind === "rate_limited");
}

/** Uma execução por vez; chamadas concorrentes devolvem `false` sem rodar. */
export function singleFlight<A extends unknown[]>(fn: (...args: A) => Promise<void>) {
  let running = false;
  return async (...args: A): Promise<boolean> => {
    if (running) return false;
    running = true;
    try {
      await fn(...args);
      return true;
    } finally {
      running = false;
    }
  };
}

/**
 * Story SF-CHAT-9.5 — troca de empresa pela UI. A lista vem do app (`GET
 * /api/v1/empresas/`); a escolhida só vale com um grant novo, que o app decide
 * de novo. A conectada não é oferecida: trocar para ela não muda nada.
 */
export type CompanySwitchPlan =
  | { kind: "only_one" }
  | { kind: "pick"; companies: { id: number; nome: string }[] };

export function planCompanySwitch(
  lista: { id: unknown; nome: string }[],
  atualId: number | null,
): CompanySwitchPlan {
  const vistos = new Set<number>();
  const outras: { id: number; nome: string }[] = [];
  for (const e of lista) {
    if (typeof e.id !== "number" || !Number.isInteger(e.id) || e.id <= 0 || vistos.has(e.id)) continue;
    vistos.add(e.id);
    if (e.id !== atualId) outras.push({ id: e.id, nome: e.nome });
  }
  return outras.length === 0 ? { kind: "only_one" } : { kind: "pick", companies: outras };
}
